import { HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

import {
  baseBoxShadowFocus,
  borderRadius16,
} from 'approot/shared/styles/borders.style';
import { flexCentreAlignCss } from 'approot/shared/styles/flex.style';
import { rem, mediaUp } from 'approot/shared/style';
import {
  PStyled,
  font18,
  fontSize18LineHeight,
  fontWeightMedium,
  font16,
  fontSize16LineHeight,
  font24,
  fontWeightLight,
  font14,
  fontSize14,
} from 'approot/shared/styles/typography.style';
import { CardButton } from './button/button';
import {
  grey1,
  grey5,
  grey6,
  grey7,
  grey8,
  grey9,
  purple7,
} from 'approot/shared/styles/colours.style';
import { Ellipsis, EllipsisProps } from 'approot/shared/styles/ellipsis.style';
import {
  cardHoverBoxShadow,
  cardHoverTransition,
} from 'approot/shared/styles/cards.style';
import {
  spacing4,
  spacing12,
  spacing16,
  spacing24,
} from 'approot/shared/styles/spacing.style';
import InlineSvg from 'approot/shared/inline-svg/inline-svg';
import { imageTransition } from 'approot/shared/images/images.style';

const cardPadding = rem(spacing16);

type ContentCardProps = {
  isDisabled?: boolean;
  ratio?: number;
  isPlaceholder?: boolean;
  contentColour?: string | null;
  noBorder?: boolean;
};

type ContentCardColourProps = {
  contentColour?: string;
  contentBackgroundColour?: string;
};

const textColourCSS = css<ContentCardColourProps>`
  ${({ contentColour }) =>
    `color: ${contentColour ? grey1 : grey8};
`}
`;

export const ContentCardBox = styled.div`
  display: block;
  position: relative;
  height: 100%;
`;

export const ContentCardContent = styled.div<ContentCardColourProps>`
  background-color: ${({ contentBackgroundColour }) =>
    contentBackgroundColour ?? grey1};
  border: 1px solid ${grey5};
  border-radius: ${rem(borderRadius16)};
  height: 100%;
  overflow: hidden;
  ${cardHoverTransition}

  &:focus-visible {
    outline: none;
    border: 1px solid ${grey1};
    box-shadow: ${baseBoxShadowFocus};
  }
`;

const anchorCss = css`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
  &:focus {
    color: inherit;
    outline: none;
  }

  &:hover + div {
    box-shadow: ${cardHoverBoxShadow};
  }

  &:focus + div {
    border: 1px solid ${grey1};
    box-shadow: ${baseBoxShadowFocus};
  }

  &:hover:focus + div {
    box-shadow: ${baseBoxShadowFocus}, ${cardHoverBoxShadow};
  }
`;

export const ContentCardLinkStyled = styled(Link)`
  ${anchorCss}
`;

export const ContentCardAnchorStyled = styled.a`
  ${anchorCss}
`;

export const ContentCard = styled.div<ContentCardProps>`
  color: ${grey9};
  text-align: left;
  overflow: hidden;
  position: relative;

  ${({ isPlaceholder, noBorder }) =>
    isPlaceholder && !noBorder ? `border: 2px dashed ${grey6};` : ''}
  ${({ contentColour }) =>
    contentColour ? `background-color: ${contentColour};` : ''}

  // MS Egge fix for padding-based aspect ratio on a flex child!
  // Instead of applying the padding to the element, we need to apply it to a pseudo element.
  // Explanation by Crisy Coyier found buried in the comments here: https://css-tricks.com/aspect-ratio-boxes/
  // Summary:
  // "...if an aspect ratio container is a child of a flex container,
  // this breaks horribly in MS Edge 15 and 16 – and maybe some recent
  // but older versions of Firefox. Padding-bottom will be 0 no matter what rule you apply."
  &::before {
    content: '';
    width: 1px;
    margin-left: -1px;
    float: left;
    height: 0;
    padding-top: ${({ ratio }) => (ratio ? ratio : 90)}%;
  }
  &::after {
    content: '';
    display: table;
    clear: both;
  }
`;

type ContentLayerProps = {
  isEditor?: boolean;
};

export const Content = styled.div`
  ${font16}
  color: ${grey9};
  padding: ${rem(spacing16)};
  text-align: left;
  position: relative;
`;

export const ContentCardPill = styled.p<ContentCardColourProps>`
  background-color: ${grey1};
  border: 1px solid ${grey5};
  align-self: flex-start;
  ${textColourCSS}
  border-radius: ${rem(borderRadius16)};
  margin-bottom: ${rem(spacing16)};
  margin-right: ${rem(88)};
  font-size: ${rem(fontSize14)};
  line-height: ${rem(fontSize16LineHeight)};
  padding: ${rem(spacing4)} ${rem(spacing12)};
`;

export const ContentCardIcon = styled(InlineSvg)`
  margin-right: ${rem(4)};
  margin-bottom: ${rem(-2)};
`;

type ContentCardTitleProps = {
  withMarginBottom?: boolean;
};

const TitleEllipsis = (
  props: HTMLAttributes<HTMLHeadingElement> & Partial<EllipsisProps>
) => <Ellipsis {...props} as="h4" $maxLines={2} />;

export const ContentCardTitle = styled(TitleEllipsis)<ContentCardTitleProps>`
  ${font18};
  font-weight: ${fontWeightMedium};
  min-height: ${rem(fontSize18LineHeight * 2)};
  max-height: ${rem(fontSize18LineHeight * 2)};
  margin-bottom: ${({ withMarginBottom }) =>
    withMarginBottom ? rem(spacing4) : 0};
  overflow: hidden;
`;

export const ContentCardDetail = styled.p`
  margin-bottom: ${rem(spacing4)};
  color: ${grey8};
`;

export const ContentCardMetaLine = styled.p`
  margin-bottom: 0;
  color: ${grey8};
`;

export const ContentCardMetaLineSecondary = styled.p`
  margin: ${rem(spacing4)} 0 0 0;
  color: ${grey8};
`;

type OverlayProps = {
  ratio?: number;
};

const ContentCardOverlayCSS = css<OverlayProps>`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  padding-top: ${({ ratio }) => (ratio ? ratio : '100')}%;
`;

type ContentCardImageProps = OverlayProps & {
  imageUrl?: string;
  imageHasLoaded: boolean;
  isLandscape: boolean;
  ignoreImageOrientation?: boolean;
};

export const ContentCardImage = styled.div.attrs<ContentCardImageProps>(
  ({ imageUrl }) => ({
    style: {
      backgroundImage: imageUrl ? `url(${imageUrl})` : '',
    },
  })
)<ContentCardImageProps>`
  ${ContentCardOverlayCSS}
  ${imageTransition}
  opacity: ${({ imageHasLoaded }) => (imageHasLoaded ? '1' : '0')};
  background-size: ${({ isLandscape, ignoreImageOrientation }) =>
    ignoreImageOrientation ? 'cover' : isLandscape ? '100% auto' : 'auto 100%'};
  background-repeat: no-repeat;
  background-position: center;
`;

export const ContentCardDarkOverlayStyled = styled.div<OverlayProps>`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  ${flexCentreAlignCss}
`;

export const ContentCardOverlayTextStyled = styled.p`
  ${font24}
  color: ${grey1};
  font-weight: ${fontWeightLight};
  margin: 0;
  text-align: center;
  padding: 0 ${rem(spacing12)};
`;

export const ContentCardEditorContainer = styled.div<OverlayProps>`
  ${ContentCardOverlayCSS}
  display: flex;
  align-items: center;
`;

export const ContentCardLayer = styled.div<OverlayProps>`
  ${ContentCardOverlayCSS}
`;

export const ActionButtonContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  padding: ${cardPadding};
  z-index: 2;
`;
export const PillContainer = styled.div<ContentLayerProps>`
  position: absolute;
  top: 0;
  padding: ${cardPadding};
  ${({ isEditor }) => (isEditor ? 'pointer-events: none;' : '')}
`;

type CardWrapperProps = {
  buttonSpace?: boolean;
};

export const ContentCardWrapper = styled.div<CardWrapperProps>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${mediaUp.sm<CardWrapperProps>`
    ${({ buttonSpace }) =>
      buttonSpace &&
      `
      padding-bottom: ${rem(70)};`}
  `}
`;

export const PlaceholderWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  text-align: center;
  display: flex;
  height: 100%;
  width: 100%;
  justify-content: center;
  flex-direction: column;
`;
export const PlaceholderText = styled(PStyled)`
  padding: ${cardPadding};
  margin: 0;
`;

export const ContentCardButton = styled(CardButton)`
  margin-top: ${rem(spacing24)};
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PlaceholderContent = styled.div`
  position: absolute;
  bottom: 0;
  padding: 0 ${cardPadding} ${cardPadding};
`;

type ContentCardEditActionsProps = {
  ratio?: number;
};
export const ContentCardEditActions = styled.div<ContentCardEditActionsProps>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding-top: ${({ ratio }) => (ratio ? ratio : 120)}%;
`;

export const ContentCardDescription = styled(PStyled)`
  ${font14}
  color: ${grey7};
  height: ${rem(90)};
  margin-bottom: ${rem(spacing12)};
  overflow: hidden;
  position: relative;
`;

export const ContentCardFeatureBadgeWrapper = styled.div`
  position: absolute;
  top: -${rem(spacing12)};
  left: ${rem(spacing16)};
`;

export const ContentCardComingSoonMessage = styled.p`
  ${font14}
  color: ${purple7};
  margin: ${rem(spacing4)} 0;
`;
